/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import MsDyn365 from '@msdyn365-commerce/core';
import { getPayloadObject, getTelemetryAttributes, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import intlTelInput from 'intl-tel-input';

export interface IAdressIntlPhoneInput {
    id?: string;
    name?: string;
    className: string;
    type: string;
    value: string;
    maxLength?: number;
    autoFocus?: boolean;
    additionalAddributes?: object;
    telemetryContent?: ITelemetryContent;
    onChange(event: React.ChangeEvent<HTMLInputElement>): void;
    scdn: string;
    countryCode?: string;
}

const AdressIntlPhoneInput: React.FC<IAdressIntlPhoneInput> = ({
    id,
    name,
    className,
    type,
    value,
    maxLength,
    additionalAddributes,
    autoFocus,
    telemetryContent,
    onChange,
    scdn,
    countryCode
}) => {
    const [inputQuery, setInputQuery] = React.useState<intlTelInput.Plugin | null>(null);

    const [displayNumber, setDisplayNumber] = React.useState(value);

    const destroyIt = () => {
        if (inputQuery) {
            inputQuery.destroy();
        }
    };
    const isValid = (intlTelInput: intlTelInput.Plugin): boolean => {
        // valid usually isValidNumber() == true, but sometime it's false but validation error is zero, which also means valid
        return intlTelInput.isValidNumber() || intlTelInput?.getValidationError() === 0;
    };

    React.useEffect(() => {
        if (id && MsDyn365.isBrowser) {
            destroyIt();

            const subId = MsDyn365.getEnvironmentVariable('SUBMISSIONID');
            const input = document.querySelector(`#${id}`);
            if (input) {
                const inputIntlTel = intlTelInput(input, {
                    utilsScript: `${scdn}${subId}/only/js/utils.js`,
                    initialCountry: countryCode ?? 'dk',
                    formatOnDisplay: true,
                    separateDialCode: true,
                    allowDropdown: true,
                    nationalMode: true,
                    preferredCountries: ['dk']
                });
                inputIntlTel.setNumber(value ?? '');
                setDisplayNumber(inputIntlTel.getNumber(2));
                setInputQuery(inputIntlTel);
            }
        }
        return destroyIt;
    }, [id]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const valueN = event.currentTarget.value;
        if (inputQuery && isValid(inputQuery)) {
            setDisplayNumber(inputQuery?.getNumber(intlTelInputUtils.numberFormat.NATIONAL) ?? valueN);
            event.target.value = inputQuery?.getNumber(0) ?? valueN;
        } else {
            // address validation is done on the address object in difficult to access code,
            // so if not valid, simpleset approach is to just make blank so required validation fails
            setDisplayNumber(valueN);
            event.target.value = '';
        }
        onChange(event);
    };

    const payLoad = getPayloadObject('click', telemetryContent!, name!);
    const attributes = getTelemetryAttributes(telemetryContent!, payLoad);
    return (
        <span className={`${className}__input ${className}__input-${type}`}>
            <input
                name={name}
                id={id}
                type={type}
                autoFocus={autoFocus}
                maxLength={maxLength}
                {...(additionalAddributes || {})}
                {...attributes}
                onChange={handleChange}
                style={{ border: 'none', width: '100%' }}
                value={displayNumber}
            />
        </span>
    );
};

export default AdressIntlPhoneInput;
